/* SHORT, READABLE NAMES FOR THE QUEUE'S CATEGORY COLUMN.
 * ===========================================================================
 *
 * `video.category` and `video.sub_category` are folder names lifted straight
 * from the S3 key — see app/api/pipeline/discover.py: `category = parts[-3]`
 * (the root folder), `sub_category = parts[-2]` (the folder holding the file).
 * The queue used to show them joined as "root · sub".
 *
 * THAT WAS LONG FOR A REASON THAT TURNED OUT NOT TO MATTER: every video
 * currently in the corpus sits under the SAME root, "Home, Household & Daily
 * Living" — one root, nine sub-folders. So "root · sub" repeated the same
 * nine words on every one of 49 rows, and the only part that actually told
 * two rows apart — the sub-folder — sat second, half the column's width in.
 * There is no third folder level (sub-sub) anywhere in the current corpus;
 * every video is exactly two levels deep under `First Person Videos/`.
 *
 * THIS IS DISPLAY-ONLY. It renames nothing in S3 and nothing in the database —
 * `video.category` / `video.sub_category` keep their long values, and every
 * filter, the discover pipeline, and any export still reads those columns
 * unchanged. This map is read in exactly one place: the queue table's
 * Category cell.
 *
 * KEYED BY THE EXACT (category, sub_category) PAIR. THE FAILURE MODE IS
 * SILENCE, same discipline as lib/featured.ts: a pair this map has not seen —
 * a folder added to S3 tomorrow, or a typo here — does not error, it just
 * falls back to today's "category · sub_category" text. A missing entry
 * degrades to the old behaviour rather than showing "undefined". */
const LABELS: Readonly<Record<string, string>> = {
  "Home, Household & Daily Living / Cleaning & Tidying": "Cleaning & Tidying",
  "Home, Household & Daily Living / Cooking & Food Preparation": "Cooking & Food Prep",
  "Home, Household & Daily Living / Gardening & Outdoor Home Activities": "Gardening & Outdoor",
  "Home, Household & Daily Living / Grocery - Food Handling & Storage": "Grocery Handling",
  "Home, Household & Daily Living / Home - Desk - Administrative Activities": "Desk & Admin",
  "Home, Household & Daily Living / Home Maintenance - DIY & Repairs": "DIY & Repairs",
  "Home, Household & Daily Living / Leisure & Hobbies": "Leisure & Hobbies",
  "Home, Household & Daily Living / Personal Care & Daily Activities": "Personal Care",
  "Home, Household & Daily Living / Pet Care": "Pet Care",
};

/** The text the queue's Category column shows for a video.
 *
 *  Falls back to the original "category · sub_category" (or whichever half is
 *  present, or "—") when the pair is not in `LABELS` — see the file header on
 *  why that fallback, and not an error, is the point. */
export function categoryLabel(
  category: string | null | undefined,
  subCategory: string | null | undefined,
): string {
  if (category && subCategory) {
    const mapped = LABELS[`${category} / ${subCategory}`];
    if (mapped) return mapped;
  }
  return [category, subCategory].filter(Boolean).join(" · ") || "—";
}
